// Mirror of Wint.Support.Service.Model.Agent. Optional fields are `T | null`: the service writes
// nulls, and the agent service hook keeps them (a null confirmToken is what makes a card read-only).

export interface AgentCardRow {
  label: string;
  value: string;
}

/** The agent needs one value before it can perform the matched action. */
export interface AgentQuestionReply {
  kind: "question";
  text: string;
  field: string;
}

export type AgentCardComponentName = "Card" | "Column" | "Field";

/** One node of the card's component tree; the service guarantees ids are unique and children resolve. */
export interface AgentCardComponent {
  id: string;
  component: AgentCardComponentName;
  /** Null on a leaf. */
  children: string[] | null;
  /** Set on a Field. */
  label: string | null;
  value: string | null;
}

/** The confirmation card. */
export interface AgentCardReply {
  kind: "card";
  actionId: string;
  title: string;
  rows: AgentCardRow[];
  /** The same facts as `rows`, as a tree; rendered instead of `rows` when present. */
  components: AgentCardComponent[] | null;
  effect: string;
  /** Text for the warning on actions that move money. */
  warning: string | null;
  /** One-time token for the confirm call. Null on a read-only card. */
  confirmToken: string | null;
}

/** The agent could not do it: a miss, a refusal or a timeout. `tried` is written for support. */
export interface AgentCaseDraftReply {
  kind: "caseDraft";
  category: string | null;
  subject: string | null;
  customerText: string;
  understood: AgentCardRow[];
  tried: string;
  suggestedPerson: string | null;
}

/** The action was performed. */
export interface AgentReceiptReply {
  kind: "receipt";
  title: string;
  body: string;
  /** Not a route in this app; not navigated to. */
  objectLink: string | null;
}

/** How the read agent's turn ended; an outcome this client does not know is read as "CouldNotAnswer". */
export type AgentOutcome =
  | "Answered"
  | "NeedsClarification"
  | "NoAccess"
  | "NotFound"
  | "CouldNotAnswer"
  | "OutOfScope"
  | "ActionNotSupported"
  | "Blocked";

/** The read agent's answer. `text` is Markdown limited to bold, bullet lists and line breaks. */
export interface AgentAnswerReply {
  kind: "answer";
  text: string;
  outcome: AgentOutcome | string;
  /** The agent ended the session (turn cap or context overflow): the next message needs a new one. */
  sessionEnded: boolean;
}

export type AgentReply =
  | AgentAnswerReply
  | AgentQuestionReply
  | AgentCardReply
  | AgentCaseDraftReply
  | AgentReceiptReply;

const KnownOutcomes: readonly string[] = [
  "Answered",
  "NeedsClarification",
  "NoAccess",
  "NotFound",
  "CouldNotAnswer",
  "OutOfScope",
  "ActionNotSupported",
  "Blocked",
];

export const outcomeOf = (reply: AgentAnswerReply): AgentOutcome =>
  KnownOutcomes.includes(reply.outcome) ? (reply.outcome as AgentOutcome) : "CouldNotAnswer";

/** Shown to her as an answer: a full one, or what the agent says about an action it cannot do. */
export const isShownAnswer = (reply: AgentAnswerReply): boolean =>
  (outcomeOf(reply) === "Answered" || outcomeOf(reply) === "ActionNotSupported") && !!reply.text.trim();

export interface AgentSessionCreated {
  sessionId: string;
}

export interface AgentMessageRequest {
  text: string;
  hasAttachments: boolean;
}

export interface AgentConversationLine {
  who: "customer" | "agent";
  text: string;
}

export interface AgentCaseSummaryRequest {
  conversation: AgentConversationLine[];
  /** The chat's language ("sv" / "en"); the subject and summary are written in it. */
  language?: string;
}

/** The case's title and category, written by the model from the conversation. */
export interface AgentCaseSummary {
  subject: string;
  category: string | null;
  /** False when the subject is her own question, without the model. */
  drafted: boolean;
}

export const isReadOnlyCard = (reply: AgentCardReply): boolean => reply.confirmToken === null;

/** What goes to support: the model's title and category, and her first question as the message. */
export interface SupportCaseContent extends AgentCaseSummary {
  /** Her first question as she wrote it. */
  question: string;
}

/** The case her question became, sent when she pressed "Skicka till support". */
export interface AgentSentCase {
  caseId: number;
  /** What support gets, as rows for her to see. */
  details: AgentCardRow[];
}

/** One row of the conversation. */
export interface AgentTurn {
  id: number;
  who: "customer" | "agent";
  at: Date;
  text?: string;
  reply?: AgentReply;
  /** The call failed or the reply kind is unknown. */
  failed?: boolean;
  /** The option she picked to answer the agent's question. */
  choice?: string;
}
